import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import {
  FormlyFieldSelectProps,
  FormlySelectModule,
} from '@ngx-formly/core/select';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import { InputTextModule } from 'primeng/inputtext';
import {
  MultiSelect,
  MultiSelectFilterEvent,
  MultiSelectLazyLoadEvent,
  MultiSelectModule,
} from 'primeng/multiselect';
import { SkeletonModule } from 'primeng/skeleton';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  debounceTime,
  firstValueFrom,
  skip,
} from 'rxjs';

interface MultiselectProps extends FormlyFieldProps, FormlyFieldSelectProps {
  query: (
    parPage: number,
    page: number,
    search?: string,
    filters?: string,
  ) => Observable<{ data: unknown[]; meta: { total: number; to: number } }>;
  pageSize?: number;
  filterPlaceholder?: string;
}

export interface FormlySelectLazyFieldConfig
  extends FormlyFieldConfig<MultiselectProps> {}

@Component({
  selector: 'kon-multiselect-lazy-type',
  standalone: true,
  imports: [
    CommonModule,
    FormlyModule,
    FormlySelectModule,
    ReactiveFormsModule,
    InputTextModule,
    SkeletonModule,
    MultiSelectModule,
  ],
  template: `
    <p-multiSelect
      #select
      [id]="id"
      [appendTo]="'body'"
      [options]="(items | formlySelectOptions : field | async) ?? []"
      [optionLabel]="'label'"
      [optionValue]="'value'"
      [formControl]="formControl"
      [showClear]="!props.required"
      (onChange)="props.change && props.change(field, $event)"
      [virtualScroll]="true"
      [filter]="true"
      [autofocusFilter]="true"
      [resetFilterOnHide]="true"
      [style]="{ width: '100%' }"
      [lazy]="true"
      [virtualScroll]="true"
      [virtualScrollItemSize]="42"
      (onLazyLoad)="onLazyLoad($event)"
      [virtualScrollOptions]="{
        step: props.pageSize ?? 50,
        showLoader: true
      }"
      (onPanelShow)="onShow()"
      (onPanelHide)="onHide()"
      (onFilter)="onSearch($event)"
    >
      <ng-template pTemplate="loader" let-options="options">
        <div class="flex items-center px-3" style="height: 42px;">
          <p-skeleton
            class="w-full"
            [width]="options.even ? '60%' : '50%'"
            height="1rem"
          ></p-skeleton>
        </div>
      </ng-template>
    </p-multiSelect>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyMultiSelectLazyTypeComponent
  extends FieldType<FieldTypeConfig<MultiselectProps>>
  implements AfterViewInit
{
  @ViewChild('select') select?: MultiSelect;

  items = new BehaviorSubject<unknown[]>([]);

  searchDebounce = new BehaviorSubject<string>('');
  search = '';

  searchSubscription?: Subscription;

  ngAfterViewInit(): void {
    if (this.select?.filterService) {
      // this disables default filtering
      this.select.filterService.register('contains', () => true);
    }
    if (this.select?.value) {
      if (Array.isArray(this.select.value) && this.select.value.length == 0) {
        return;
      }

      this.freshLoad(undefined, this.select.value);
    }
  }

  async freshLoad(search = '', itemValue: unknown = undefined) {
    this.select?.scroller?.scrollToIndex(0);
    this.search = search;
    let filter: string | undefined = undefined;

    if (itemValue) {
      filter = Array.isArray(itemValue)
        ? itemValue
            .map((value) => `${this.props.valueProp} eq "${value}"`)
            .join(' or ')
        : `${this.props.valueProp} eq "${itemValue}"`;
    }

    const response = await firstValueFrom(
      this.props.query(this.pageSize(), 1, search, filter),
    );
    this.patchItems(0, response.data, response.meta.total);
    this.select?.focusedOptionIndex.set(0); // focus first item
  }

  async onLazyLoad(event: MultiSelectLazyLoadEvent) {
    const total = this.items.value.length;
    const first =
      event.last >= total && total > 0
        ? Math.floor(event.last / this.pageSize()) * this.pageSize()
        : Math.max(event.first, 0); // prevent negative first
    const page = Math.floor(first / this.pageSize()) + 1;

    const response = await firstValueFrom(
      this.props.query(this.pageSize(), page, this.search),
    );

    this.patchItems(first, response.data, response.meta.total);
  }

  onSearch(event: MultiSelectFilterEvent) {
    if (event.filter === this.searchDebounce.value) return;
    this.searchDebounce.next(event.filter);
  }

  patchItems(start: number, items: unknown[], total: number) {
    let newItems = [...this.items.value];
    if (newItems.length != total) {
      newItems = Array.from({ length: total }, function () {
        return {};
      });
    }
    let idx = start;
    items.forEach((item) => {
      newItems[idx] = item;
      idx++;
    });
    this.items.next(newItems);
  }

  onShow() {
    this.freshLoad();
    this.searchSubscription = this.searchDebounce
      .pipe(skip(1), debounceTime(500))
      .subscribe((search) => {
        this.freshLoad(search);
      });
  }

  onHide() {
    this.searchSubscription?.unsubscribe();
    this.searchDebounce.next('');
  }

  pageSize = () => this.props.pageSize ?? 50;
}
